/**
 * BookingRefreshContext — the shell→page refresh signal (spec C3,
 * 2026-07-09 admin shell wave).
 *
 * Quick Book now lives in the shell topbar (AppShell mounts the modal on
 * every page). When a booking succeeds, pages showing operational data
 * (Dashboard widgets today; the Lane-D hero next) must refresh without a
 * reload. The shell bumps `version` after each successful booking;
 * subscribers fold it into their own refresh keys.
 *
 * Deliberately tiny: a monotonic counter, no payload — consumers refetch
 * from the source of truth rather than trusting a pushed object
 * (Principle 1: numbers that could disagree must come from one source).
 *
 * The default value keeps standalone mounts (unit harnesses that render
 * a page without AppShell) working: version never changes, bump no-ops.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type BookingRefreshValue = {
  /** Monotonic counter — increments once per successful shell booking. */
  version: number;
  /** Signal a completed booking (shell-side; pages normally only read). */
  bump: () => void;
};

const BookingRefreshContext = createContext<BookingRefreshValue>({
  version: 0,
  bump: () => {},
});

export function BookingRefreshProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const value = useMemo(() => ({ version, bump }), [version, bump]);
  return (
    <BookingRefreshContext.Provider value={value}>
      {children}
    </BookingRefreshContext.Provider>
  );
}

/** Subscribe to the shell's booking-success signal. */
export function useBookingRefresh(): BookingRefreshValue {
  return useContext(BookingRefreshContext);
}
